import {
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    type DataGridProps,
    DataGridRow,
    makeStyles,
    type TableColumnDefinition,
    Toolbar,
    ToolbarGroup,
} from '@fluentui/react-components';
import { memo, type ReactNode } from 'react';
import { MessageBarGroup } from '../message-bar-group/MessageBarGroup';
import { useToolbarStyles } from '../toolbar/toolbar-styles';
import { ToolbarOverflowGroup } from '../toolbar/ToolbarOverflowGroup';
import { useListPaginationQuerySortFragmentRef } from './pagination/list-pagination-query';
import type { listPaginationQueryFragment$key } from './pagination/__generated__/listPaginationQueryFragment.graphql';
import { ListPagination } from './pagination/ListPagination';
import { ListSearch } from './search/ListSearch';
import type { IQueryListRouteApiProps } from './QueryListRouteApi';

const useQueryListStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateRows: 'auto minmax(0, 1fr)',
        gap: '1em',
        minHeight: 0,
        height: '100%', // füllt main aus
        overflow: 'hidden',
    },
    listScrollContainer: {
        overflow: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
    },
});

export const QueryList = memo<IQueryListProps>((props) => {
    const styles = useQueryListStyles();
    return (
        <div className={styles.root}>
            <Header {...props.toolbar} pagination={props.pagination} routeApi={props.routeApi} />
            <div className={styles.listScrollContainer}>
                <List
                    columns={props.columns}
                    items={props.items}
                    pagination={props.pagination}
                    routeApi={props.routeApi}
                    selectionMode={props.selectionMode}
                    toolbar={props.toolbar}
                    columnSizingOptions={props.columnSizingOptions}
                />
            </div>
        </div>
    );
});
QueryList.displayName = 'QueryList';

export interface IQueryListProps extends IQueryListRouteApiProps, IListProps {
    toolbar: Omit<IToolbarProps, 'pagination' | 'routeApi'>;
    columnSizingOptions?: DataGridProps['columnSizingOptions'];
}

const Header = memo<IToolbarProps>((props) => {
    const toolbarStyles = useToolbarStyles();

    return (
        <div className={useHeaderStyles().root}>
            <MessageBarGroup />

            <Toolbar className={toolbarStyles.toolbar} size="small">
                <ToolbarGroup className={toolbarStyles.toolbarGroup}>{props.items}</ToolbarGroup>

                <ToolbarOverflowGroupItems>{props.overflowItems}</ToolbarOverflowGroupItems>

                <ToolbarGroup className={toolbarStyles.toolbarGroup}>{props.farItems}</ToolbarGroup>
            </Toolbar>

            <SearchAndPagination pagination={props.pagination} routeApi={props.routeApi} />
        </div>
    );
});
Header.displayName = 'Header';

interface IToolbarProps extends IQueryListRouteApiProps, IPaginationProps {
    farItems: ReactNode[];
    items: ReactNode[];
    overflowItems: InstanceType<typeof ToolbarOverflowGroup.Item>[];
}

interface IPaginationProps {
    pagination?: listPaginationQueryFragment$key;
}

const useHeaderStyles = makeStyles({
    root: {
        display: 'grid',
        gap: '1em',
    },
});

const ToolbarOverflowGroupItems = (props: { children: IToolbarProps['overflowItems'] }) => (
    <ToolbarOverflowGroup.Group>
        {props.children.map((entry) => (
            <entry.Button key={entry.id} />
        ))}

        <ToolbarOverflowGroup.Menu>
            {props.children.map((entry) => (
                <entry.MenuItem key={entry.id} />
            ))}
        </ToolbarOverflowGroup.Menu>
    </ToolbarOverflowGroup.Group>
);

const SearchAndPagination = memo<IQueryListRouteApiProps & IPaginationProps>((props) => (
    <div className={useSearchAndPaginationStyles().root}>
        <ListSearch routeApi={props.routeApi} />
        <ListPagination fragmentRef={props.pagination} routeApi={props.routeApi} />
    </div>
));

const useSearchAndPaginationStyles = makeStyles({
    root: {
        display: 'flex',
        flexWrap: 'nowrap',
        gap: '1em',
        '>:first-child': {
            flexGrow: 1,
        },
    },
});

const List = memo<IQueryListProps>((props) => {
    const { routeApi, columnSizingOptions } = props;
    const items = props.items || [];
    const selectionState = routeApi.useSearch({
        select: (state) => state.selected,
    });
    const sort = useListPaginationQuerySortFragmentRef(props.pagination)?.orders[0];
    const navigate = routeApi.useNavigate();

    return (
        <DataGrid
            resizableColumns
            columnSizingOptions={columnSizingOptions}
            // className="dt-selection-single"
            columns={props.columns}
            getRowId={(item) => item.id}
            items={items as any}
            selectedItems={
                Array.isArray(selectionState) && items.length > 0 && selectionState.length > 0
                    ? items
                          .filter((item) => selectionState.includes(item.id))
                          .map((item) => item.id)
                    : []
            }
            selectionMode={props.selectionMode}
            sortable
            // @ts-ignore
            sortState={
                sort?.direction && sort?.property
                    ? {
                          sortColumn: sort.property,
                          sortDirection: sort.direction === 'ASC' ? 'ascending' : 'descending',
                      }
                    : {
                          // sortColumn: props.columns[0].columnId,
                          sortDirection: 'ascending',
                      }
            }
            onSelectionChange={(_e, { selectedItems }) => {
                navigate({
                    search: (prevSearch) => ({
                        ...prevSearch,
                        selected: selectedItems.size > 0 ? Array.from(selectedItems) : undefined,
                    }),
                });
            }}
            onSortChange={(_e, sortState): void => {
                navigate({
                    search: (prevSearch) => ({
                        ...prevSearch,
                        sortBy: sortState.sortColumn,
                        sortDir: sortState.sortDirection.replace('ending', '').toUpperCase(),
                    }),
                });
            }}
        >
            <DataGridHeader>
                <DataGridRow>
                    {(column) => (
                        <DataGridHeaderCell>{column.renderHeaderCell()}</DataGridHeaderCell>
                    )}
                </DataGridRow>
            </DataGridHeader>

            <DataGridBody>
                {(row) => (
                    <DataGridRow key={row.rowId}>
                        {(column) => <DataGridCell>{column.renderCell(row.item)}</DataGridCell>}
                    </DataGridRow>
                )}
            </DataGridBody>
        </DataGrid>
    );
});
List.displayName = 'List';

interface IListProps<I extends IItem = any>
    extends IQueryListRouteApiProps, IPaginationProps, Pick<DataGridProps, 'selectionMode'> {
    readonly columns: TableColumnDefinition<I>[];
    readonly items?: ReadonlyArray<I>;
}

interface IItem {
    readonly id: string | number;
    readonly [key: string]: any;
}
